import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Music2, MapPin } from "lucide-react";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { JOBBER_BOOKING_URL } from "../lib/brand";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Me" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [socials, setSocials] = useState({ instagram: "#", facebook: "#", tiktok: "#", google_business: "#" });
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    api.get("/socials").then((r) => setSocials(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display font-semibold tracking-wide uppercase text-sm transition-colors ${
                    isActive ? "text-[#D90429]" : "text-white hover:text-[#D90429]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={JOBBER_BOOKING_URL}
              data-testid="nav-book-now"
              className="bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase text-sm px-6 py-3 transition-colors"
            >
              Book Now
            </a>
          </nav>
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-black/90">
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display font-semibold uppercase text-base ${isActive ? "text-[#D90429]" : "text-white"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={JOBBER_BOOKING_URL}
                data-testid="nav-mobile-book-now"
                className="bg-[#D90429] text-white text-center font-display font-bold uppercase tracking-wider px-6 py-3"
              >
                Book Now
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/10 bg-[#050505] mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-12">
          <div>
            <Logo size="md" />
            <p className="mt-6 text-[#A3A3A3] text-sm leading-relaxed max-w-xs">
              Premium mobile car detailing engineered for showroom finishes. Built on precision and pride.
            </p>
          </div>
          <div>
            <h4 className="font-display uppercase tracking-[0.2em] text-xs text-[#D90429] mb-4">Navigate</h4>
            <ul className="space-y-2">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm hover:text-[#D90429] transition-colors">{l.label}</Link>
                </li>
              ))}
              <li><a href={JOBBER_BOOKING_URL} data-testid="footer-book-now" className="text-sm hover:text-[#D90429] transition-colors">Book Now</a></li>
              <li><Link to="/privacy" data-testid="footer-privacy-link" className="text-sm hover:text-[#D90429] transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display uppercase tracking-[0.2em] text-xs text-[#D90429] mb-4">Follow</h4>
            <div className="flex gap-3">
              <a href={socials.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram"
                 className="w-10 h-10 border border-[#262626] hover:border-[#D90429] hover:text-[#D90429] flex items-center justify-center transition-colors"><Instagram size={18}/></a>
              <a href={socials.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook"
                 className="w-10 h-10 border border-[#262626] hover:border-[#D90429] hover:text-[#D90429] flex items-center justify-center transition-colors"><Facebook size={18}/></a>
              <a href={socials.tiktok} target="_blank" rel="noreferrer" data-testid="footer-tiktok"
                 className="w-10 h-10 border border-[#262626] hover:border-[#D90429] hover:text-[#D90429] flex items-center justify-center transition-colors"><Music2 size={18}/></a>
              <a href={socials.google_business} target="_blank" rel="noreferrer" data-testid="footer-google"
                 className="w-10 h-10 border border-[#262626] hover:border-[#D90429] hover:text-[#D90429] flex items-center justify-center transition-colors"><MapPin size={18}/></a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-[#A3A3A3]">
            <span>© {new Date().getFullYear()} Fuego Precision Detailing. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-[#D90429]" data-testid="footer-privacy-copyright-link">Privacy Policy</Link>
              <Link to="/admin" className="hover:text-[#D90429]" data-testid="footer-admin-link">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
